/**
 * Capability helpers — kept as pure functions so they can be unit-tested and
 * composed without a Context read. The source of truth is /api/me returning
 * `effective_capabilities` (role defaults + GRANTs − DENYs, CEO gets '*').
 *
 * RULE: UI hiding uses these helpers (UX). Real authorization stays server-side
 * on every endpoint (SECURITY). Both are required — do NOT drop backend checks
 * because the client hides a button.
 */

// Which capabilities are considered "administrative" (i.e. reveal admin dashboards,
// admin ferie console, personale/team, etc.). Any of these switches the user
// into the admin UI instead of the employee self-service UI.
const ADMIN_CAP_PREFIXES = [
  'security.users',
  'security.roles',
  'personale',
  'ferie.approve',
  'ferie.balance',
  'ferie.config',
  'audit',
  'company',
  'location',
  'department',
  'haccp',
];

// Modules that render dedicated nav entries once ANY capability in the family exists.
const MODULE_PREFIXES = {
  orders:    'orders',
  warehouse: 'warehouse',
  turni:     'turni',
  attendance:'attendance',
};

const CEO_WILDCARD = '*';

function _caps(me) {
  if (!me) return [];
  return me.effective_capabilities || [];
}

/** True if the user holds the given capability, considering CEO wildcard,
 *  family wildcard (`orders.*` covers `orders.fish.create`) and exact match. */
export function hasCap(me, code) {
  if (!me) return false;
  if (me.is_global) return true;
  const caps = _caps(me);
  if (caps.includes(CEO_WILDCARD)) return true;
  if (caps.includes(code)) return true;
  const family = code.split('.', 1)[0] + '.*';
  if (caps.includes(family)) return true;
  return false;
}

/** True if the user holds ANY capability whose code starts with the prefix,
 *  including the family wildcard (`orders.*`). Used to decide whether a NAV
 *  entry / dashboard card should render at all. */
export function hasAnyCapUnder(me, prefix) {
  if (!me) return false;
  if (me.is_global) return true;
  const caps = _caps(me);
  if (caps.includes(CEO_WILDCARD)) return true;
  return caps.some((c) => c === `${prefix}.*` || c === prefix || c.startsWith(`${prefix}.`));
}

/** True if the user has NO admin-level capability — should see the employee
 *  self-service UI (personal dashboard, "Le mie ferie", "I miei documenti"). */
export function isPureEmployee(me) {
  if (!me) return false;
  if (me.is_global) return false;
  for (const p of ADMIN_CAP_PREFIXES) {
    if (hasAnyCapUnder(me, p)) return false;
  }
  // At least one non-admin capability must exist, otherwise this is an unbound
  // account (shouldn't happen in practice — treat as employee for safety).
  return true;
}

/** Convenience shortcuts used throughout the UI. */
export function canApproveFerie(me)   { return hasCap(me, 'ferie.approve'); }
export function canViewPersonale(me)  { return hasCap(me, 'personale.view'); }
export function isGlobalCeo(me) {
  return Boolean(me?.is_global && (me.role_codes || []).includes('CEO'));
}
// Tenant-scoped employee access management. This is intentionally broader
// than the Foundation console: TITOLARE accounts use the same operational
// flow as the CEO when working inside their own tenant.
export function canViewOperationalUsers(me) { return hasCap(me, 'security.users.view'); }
// Foundation user management remains CEO-only and is reserved for managing
// Titolare accounts globally.
export function canViewUsers(me)      { return isGlobalCeo(me) && hasCap(me, 'security.users.view'); }
export function canOrderAnything(me)  { return hasAnyCapUnder(me, MODULE_PREFIXES.orders); }
export function canAccessWarehouse(me){ return hasAnyCapUnder(me, MODULE_PREFIXES.warehouse); }
export function canViewTurni(me)      { return hasCap(me, 'turni.view'); }
export function canManageOwnAvailability(me) { return hasCap(me, 'availability.self.manage'); }
export function canSelfAttendance(me) { return hasCap(me, 'attendance.self'); }
